import RoomStayBooking from './RoomStayBooking';
import GroupBooking from './GroupBooking';
import CorporateBooking from './CorporateBooking';
import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F4FF', text2:'#8A9BB8', text3:'#4A5A78',
 accent:'#3AAF58', blue:'#FFFFFF', blueLight:'#FFFFFF',
 red:'#E05050', redBg:'rgba(224,80,80,.10)', redBorder:'rgba(224,80,80,.2)',
 amber:'#D4840F', amberBg:'rgba(212,132,15,.10)', amberBorder:'rgba(212,132,15,.2)',
 green:'#3AAF58', greenBg:'rgba(58,175,88,.10)', greenBorder:'rgba(58,175,88,.2)',
 purple:'#8060C0', teal:'#0D9488',
};

const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}) : '—';
const fmtTime = () => new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
const todayStr = new Date().toISOString().slice(0,10);
const tomorrowStr = new Date(Date.now()+86400000).toISOString().slice(0,10);

// ── KPI CARD ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon, color, bg, onClick, sub }) {
 return (
 <div onClick={onClick} style={{ background:bg||C.surface, border:`1px solid ${color}33`, borderRadius:14, padding:'16px 18px', cursor:onClick?'pointer':'default', transition:'transform .15s', position:'relative', overflow:'hidden' }}>
 <div style={{ position:'absolute', top:10, right:14, fontSize:28, opacity:.15 }}>{icon}</div>
 <div style={{ fontSize:11, color:C.text2, fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>{label}</div>
 <div style={{ fontSize:32, fontWeight:900, color:color||C.text, lineHeight:1 }}>{value}</div>
 {sub && <div style={{ fontSize:11, color:C.text3, marginTop:4 }}>{sub}</div>}
 </div>
 );
}

// ── QUICK ACTION BUTTON ───────────────────────────────────────────────────────
function QuickAction({ icon, label, color, onClick }) {
 return (
 <button onClick={onClick} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, padding:'16px 12px', background:C.surface2, border:`1px solid ${color||C.border2}`, borderRadius:12, cursor:'pointer', color:color||C.text2, transition:'all .2s', width:'100%' }}>
 <div style={{ fontSize:24 }}>{icon}</div>
 <div style={{ fontSize:11, fontWeight:600, color:color||C.text2, textAlign:'center', lineHeight:1.3 }}>{label}</div>
 </button>
 );
}

// ── ARRIVAL CARD ──────────────────────────────────────────────────────────────
function BookingCard({ booking, onCheckin }) {
 const btype = booking.booking_type || '';
 const isVIP = booking.pax_count > 20 || btype.includes('Wedding');
 return (
 <div style={{ background:C.surface2, border:`1px solid ${C.border}`, borderRadius:10, padding:12, marginBottom:8 }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
 <div>
 <div style={{ display:'flex', alignItems:'center', gap:6 }}>
 <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{booking.guest_name||booking.company_name||'—'}</div>
 {isVIP && <span style={{ padding:'1px 6px', background:'rgba(212,132,15,.2)', borderRadius:4, fontSize:9, fontWeight:800, color:C.amber }}>VIP</span>}
 </div>
 <div style={{ fontSize:11, color:C.text2, marginTop:2 }}>{btype} · {booking.pax_count} pax</div>
 <div style={{ fontSize:11, color:C.text3 }}>{booking.booking_code} · {booking.source}</div>
 </div>
 <div style={{ textAlign:'right' }}>
 <div style={{ fontSize:13, fontWeight:700, color:C.accent }}>{inr(booking.total_amount)}</div>
 {parseFloat(booking.balance_amount)>0 && (
 <div style={{ fontSize:11, color:C.red }}>Bal: {inr(booking.balance_amount)}</div>
 )}
 </div>
 </div>
 <div style={{ display:'flex', gap:6 }}>
 <button onClick={()=>onCheckin(booking)}
 style={{ flex:1, padding:'6px', background:`${C.accent}20`, border:`1px solid ${C.accent}44`, borderRadius:6, color:C.accent, cursor:'pointer', fontSize:11, fontWeight:700 }}>
 Check In
 </button>
 <button style={{ padding:'6px 10px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:11 }}>
 Profile
 </button>
 <button style={{ padding:'6px 10px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:11 }}>
 Reg Card
 </button>
 </div>
 </div>
 );
}

// ── CHECKIN MODAL ─────────────────────────────────────────────────────────────
function CheckinModal({ booking, rooms, roomTypes, onConfirm, onClose }) {
 const [assignedRoom, setAssignedRoom] = useState('');
 const [vehicleNo, setVehicleNo] = useState('');
 const [idVerified, setIdVerified] = useState(false);
 const [collectBal, setCollectBal] = useState(false);
 const [welcome, setWelcome] = useState(true);
 const balance = parseFloat(booking.balance_amount||0);
 const inp2 = { width:'100%', padding:'8px 12px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box', outline:'none' };

 const vacantRooms = rooms.filter(r => r.status==='Vacant Clean');

 return (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.8)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:16, overflowY:'auto' }}>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, width:'100%', maxWidth:640, marginTop:20 }}>
 <div style={{ padding:'16px 22px', background:'linear-gradient(135deg,#0A2010,#1A3A20)', borderRadius:'16px 16px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <div>
 <div style={{ fontWeight:800, fontSize:16, color:'#fff' }}> Check-in</div>
 <div style={{ fontSize:12, color:'rgba(255,255,255,.5)', marginTop:2 }}>{booking.booking_code}</div>
 </div>
 <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,.5)', fontSize:20, cursor:'pointer' }}></button>
 </div>
 <div style={{ padding:22 }}>
 {/* Guest Info */}
 <div style={{ background:C.surface2, borderRadius:10, padding:14, marginBottom:16 }}>
 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
 {[
 ['Guest', booking.guest_name||booking.company_name||'—'],
 ['Type', booking.booking_type],
 ['Pax', `${booking.pax_count} guests`],
 ['Meal Plan', booking.package_name||'As booked'],
 ['Balance', balance>0?inr(balance):' Paid'],
 ['Source', booking.source],
 ].map(([l,v])=>(
 <div key={l}>
 <div style={{ fontSize:10, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em' }}>{l}</div>
 <div style={{ fontSize:13, fontWeight:600, color:balance>0&&l==='Balance'?C.red:C.text, marginTop:2 }}>{v}</div>
 </div>
 ))}
 </div>
 </div>

 {/* Assign Room */}
 <div style={{ marginBottom:14 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Assign Room</label>
 <select value={assignedRoom} onChange={e=>setAssignedRoom(e.target.value)} style={{ ...inp2, cursor:'pointer' }}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="">— Select Room —</option>
 {vacantRooms.map(r => {
 const rt = roomTypes.find(t=>t.id===r.room_type_id);
 return <option style={{background:'#0F2040',color:'#F0F2FF'}} key={r.id} value={r.room_number}>#{r.room_number} — {r.block_name||''} {rt?.name||'Room'}</option>;
 })}
 </select>
 </div>

 {/* Vehicle */}
 <div style={{ marginBottom:14 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Vehicle Number</label>
 <input style={inp2} type="text" value={vehicleNo} onChange={e=>setVehicleNo(e.target.value)} placeholder="DL 01 AB 1234" />
 </div>

 {/* Checklist */}
 <div style={{ background:C.surface2, borderRadius:10, padding:14, marginBottom:16 }}>
 <div style={{ fontSize:12, fontWeight:700, color:C.text2, marginBottom:10, textTransform:'uppercase', letterSpacing:'.06em' }}>Check-in Checklist</div>
 {[
 { key:'id', label:'ID Verified (Aadhaar/Passport)', state:idVerified, set:setIdVerified },
 { key:'bal', label:`Collect Balance ${balance>0?inr(balance):'(Paid)'}`, state:collectBal, set:setCollectBal },
 { key:'welcome', label:'Welcome Drink Issued', state:welcome, set:setWelcome },
 ].map(item=>(
 <label key={item.key} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, cursor:'pointer' }}>
 <input type="checkbox" checked={item.state} onChange={e=>item.set(e.target.checked)} />
 <span style={{ fontSize:13, color:C.text2 }}>{item.label}</span>
 </label>
 ))}
 </div>

 {/* Action buttons */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:16 }}>
 {[
 {label:'Print Reg Card'},
 {label:'Print Meal Coupon'},
 {label:'Send WhatsApp'},
 ].map(b=>(
 <button key={b.label} style={{ padding:'10px 6px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:12, fontWeight:600 }}>
 {b.icon} {b.label}
 </button>
 ))}
 </div>

 <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
 <button onClick={onClose} style={{ padding:'10px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={()=>onConfirm({assignedRoom,vehicleNo,idVerified,collectBal})}
 style={{ padding:'10px 24px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14 }}>
 Complete Check-in
 </button>
 </div>
 </div>
 </div>
 </div>
 );
}

// ── GUEST REQUESTS MODULE ─────────────────────────────────────────────────────
function GuestRequests() {
 const REQUESTS = ['Extra Pillow','Baby Cot','Wake Up Call','Room Cleaning','Taxi','Packed Breakfast','Late Checkout','Medical Help','Extra Towel','Iron & Board','Room Service'];
 const [requests, setRequests] = useState([
 {id:1,guest:'Room 101',type:'Extra Pillow',time:'09:15',status:'Pending'},
 {id:2,guest:'Room 203',type:'Wake Up Call',time:'09:30',status:'Done'},
 {id:3,guest:'Room 305',type:'Taxi',time:'10:00',status:'In Progress'},
 ]);
 const [showAdd, setShowAdd] = useState(false);
 const [form, setForm] = useState({guest:'',type:'Extra Pillow',note:''});

 const STATUS_C = {'Pending':C.amber,'In Progress':C.blue,'Done':C.green,'Cancelled':C.red};
 const inp2 = { width:'100%', padding:'8px 12px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box', outline:'none' };

 return (
 <div>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text }}> Guest Requests</div>
 <button onClick={()=>setShowAdd(true)} style={{ padding:'7px 14px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600 }}>+ New Request</button>
 </div>
 <div style={{ display:'grid', gap:8 }}>
 {requests.map(r=>(
 <div key={r.id} style={{ background:C.surface2, border:`1px solid ${C.border}`, borderRadius:10, padding:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <div>
 <div style={{ fontWeight:600, fontSize:13, color:C.text }}>{r.type}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{r.guest} · {r.time}</div>
 </div>
 <div style={{ display:'flex', gap:8, alignItems:'center' }}>
 <select value={r.status} onChange={e=>setRequests(p=>p.map(x=>x.id===r.id?{...x,status:e.target.value}:x))}
 style={{ ...inp2, width:130, padding:'5px 8px', fontSize:12 }}>
 {['Pending','In Progress','Done','Cancelled'].map(s=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 <span style={{ padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:700, background:`${STATUS_C[r.status]||C.text3}20`, color:STATUS_C[r.status]||C.text3 }}>{r.status}</span>
 </div>
 </div>
 ))}
 </div>
 {showAdd && (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:24, maxWidth:420, width:'100%' }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text, marginBottom:16 }}>New Guest Request</div>
 <div style={{ marginBottom:12 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Room / Guest</label>
 <input style={inp2} type="text" value={form.guest} onChange={e=>setForm(p=>({...p,guest:e.target.value}))} placeholder="Room 101 or Guest Name" />
 </div>
 <div style={{ marginBottom:12 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Request Type</label>
 <select style={{ ...inp2, cursor:'pointer' }} value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}>
 {REQUESTS.map(r=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={r}>{r}</option>)}
 </select>
 </div>
 <div style={{ marginBottom:16 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Note</label>
 <input style={inp2} type="text" value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))} placeholder="Additional details" />
 </div>
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
 <button onClick={()=>setShowAdd(false)} style={{ padding:'9px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={()=>{
 setRequests(p=>[...p,{id:Date.now(),guest:form.guest,type:form.type,time:new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}),status:'Pending'}]);
 setShowAdd(false); setForm({guest:'',type:'Extra Pillow',note:''});
 }} style={{ padding:'9px 22px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700 }}>Add Request</button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ── LOST & FOUND ──────────────────────────────────────────────────────────────
function LostFound() {
 const [items, setItems] = useState([
 {id:1,item:'Wallet',foundBy:'Housekeeping',location:'Room 203',guest:'Mr. Sharma',status:'Pending',date:'2026-07-22'},
 {id:2,item:'Charger',foundBy:'F&B',location:'Restaurant',guest:'Unknown',status:'Returned',date:'2026-07-21'},
 ]);
 const [showAdd, setShowAdd] = useState(false);
 const [form, setForm] = useState({item:'',foundBy:'',location:'',guest:'',note:''});
 const inp2 = { width:'100%', padding:'8px 12px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box', outline:'none' };

 return (
 <div>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text }}> Lost & Found</div>
 <button onClick={()=>setShowAdd(true)} style={{ padding:'7px 14px', background:C.amber, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600 }}>+ Report Item</button>
 </div>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
 <table style={{ width:'100%', borderCollapse:'collapse' }}>
 <thead>
 <tr style={{ borderBottom:`1px solid ${C.border}` }}>
 {['Item','Found By','Location','Guest','Date','Status','Action'].map(h=>(
 <th key={h} style={{ padding:'10px 14px', fontSize:11, color:C.text2, fontWeight:600, textAlign:'left', textTransform:'uppercase' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {items.map(item=>(
 <tr key={item.id} style={{ borderBottom:`1px solid ${C.border}` }}>
 <td style={{ padding:'10px 14px', fontWeight:600, color:C.text }}>{item.item}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{item.foundBy}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{item.location}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{item.guest}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{item.date}</td>
 <td style={{ padding:'10px 14px' }}>
 <span style={{ padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:700, background:item.status==='Returned'?C.greenBg:C.amberBg, color:item.status==='Returned'?C.green:C.amber }}>{item.status}</span>
 </td>
 <td style={{ padding:'10px 14px' }}>
 {item.status==='Pending' && (
 <button onClick={()=>setItems(p=>p.map(x=>x.id===item.id?{...x,status:'Returned'}:x))}
 style={{ padding:'4px 10px', background:C.greenBg, border:`1px solid ${C.greenBorder||C.accent}`, borderRadius:6, color:C.green, cursor:'pointer', fontSize:11 }}>
 Mark Returned
 </button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 {showAdd && (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:24, maxWidth:460, width:'100%' }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text, marginBottom:16 }}>Report Lost & Found Item</div>
 {[
 ['Item Description','item','text','e.g. Black wallet, iPhone charger'],
 ['Found By','foundBy','text','Staff name/department'],
 ['Location Found','location','text','Room 203, Restaurant, Pool'],
 ['Guest Name','guest','text','If known'],
 ['Notes','note','text','Additional details'],
 ].map(([l,k,t,p])=>(
 <div key={k} style={{ marginBottom:12 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>{l}</label>
 <input style={inp2} type={t} value={form[k]} onChange={e=>setForm(p2=>({...p2,[k]:e.target.value}))} placeholder={p} />
 </div>
 ))}
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
 <button onClick={()=>setShowAdd(false)} style={{ padding:'9px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={()=>{
 setItems(p=>[...p,{id:Date.now(),...form,status:'Pending',date:todayStr}]);
 setShowAdd(false); setForm({item:'',foundBy:'',location:'',guest:'',note:''});
 }} style={{ padding:'9px 22px', background:C.amber, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700 }}>Save Item</button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ── MAINTENANCE TICKETS ───────────────────────────────────────────────────────
function MaintenanceTickets() {
 const ISSUES = ['AC Not Working','Leakage','TV Issue','Door Lock','Hot Water','Electricity','WiFi','Plumbing','Furniture Damage','Other'];
 const [tickets, setTickets] = useState([
 {id:1,room:'Room 203',issue:'AC Not Working',priority:'High',status:'Open',time:'08:30'},
 {id:2,room:'Room 105',issue:'Hot Water',priority:'Medium',status:'In Progress',time:'09:15'},
 ]);
 const [showAdd, setShowAdd] = useState(false);
 const [form, setForm] = useState({room:'',issue:'AC Not Working',priority:'Medium',note:''});
 const inp2 = { width:'100%', padding:'8px 12px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box', outline:'none' };
 const STATUS_C = {'Open':C.red,'In Progress':C.amber,'Completed':C.green,'Assigned':C.blue};
 const PRIORITY_C = {'High':C.red,'Medium':C.amber,'Low':C.green};

 return (
 <div>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text }}> Maintenance Tickets</div>
 <button onClick={()=>setShowAdd(true)} style={{ padding:'7px 14px', background:C.red, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600 }}>+ Raise Ticket</button>
 </div>
 <div style={{ display:'grid', gap:8 }}>
 {tickets.map(t=>(
 <div key={t.id} style={{ background:C.surface2, border:`1px solid ${STATUS_C[t.status]||C.border}33`, borderRadius:10, padding:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <div>
 <div style={{ display:'flex', alignItems:'center', gap:8 }}>
 <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{t.issue}</div>
 <span style={{ padding:'2px 6px', borderRadius:4, fontSize:9, fontWeight:800, background:`${PRIORITY_C[t.priority]||C.text3}20`, color:PRIORITY_C[t.priority]||C.text3 }}>{t.priority}</span>
 </div>
 <div style={{ fontSize:11, color:C.text3 }}>{t.room} · {t.time}</div>
 </div>
 <div style={{ display:'flex', gap:8, alignItems:'center' }}>
 <select value={t.status} onChange={e=>setTickets(p=>p.map(x=>x.id===t.id?{...x,status:e.target.value}:x))}
 style={{ ...inp2, width:140, padding:'5px 8px', fontSize:12 }}>
 {['Open','Assigned','In Progress','Completed'].map(s=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 </div>
 </div>
 ))}
 </div>
 {showAdd && (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:24, maxWidth:420, width:'100%' }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text, marginBottom:16 }}>Raise Maintenance Ticket</div>
 {[['Room / Location','room','text','e.g. Room 203, Pool Area']].map(([l,k,t,p])=>(
 <div key={k} style={{ marginBottom:12 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>{l}</label>
 <input style={inp2} type={t} value={form[k]} onChange={e=>setForm(p2=>({...p2,[k]:e.target.value}))} placeholder={p} />
 </div>
 ))}
 <div style={{ marginBottom:12 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Issue Type</label>
 <select style={{ ...inp2, cursor:'pointer' }} value={form.issue} onChange={e=>setForm(p=>({...p,issue:e.target.value}))}>
 {ISSUES.map(i=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={i}>{i}</option>)}
 </select>
 </div>
 <div style={{ marginBottom:12 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Priority</label>
 <select style={{ ...inp2, cursor:'pointer' }} value={form.priority} onChange={e=>setForm(p=>({...p,priority:e.target.value}))}>
 {['High','Medium','Low'].map(p=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={p}>{p}</option>)}
 </select>
 </div>
 <div style={{ marginBottom:16 }}>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Note</label>
 <input style={inp2} type="text" value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))} placeholder="Additional details" />
 </div>
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
 <button onClick={()=>setShowAdd(false)} style={{ padding:'9px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={()=>{
 setTickets(p=>[...p,{id:Date.now(),...form,status:'Open',time:new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}]);
 setShowAdd(false); setForm({room:'',issue:'AC Not Working',priority:'Medium',note:''});
 }} style={{ padding:'9px 22px', background:C.red, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700 }}>Raise Ticket</button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ── MAIN FRONT OFFICE DASHBOARD ───────────────────────────────────────────────
export default function FrontOfficeDashboard() {
 const [activeTab, setActiveTab] = useState('dashboard');

 const handleLogout = () => {
 localStorage.removeItem('botanix_user');
 localStorage.removeItem('botanix_token');
 window.location.reload();
 };
 const [bookings, setBookings] = useState([]);
 const [rooms, setRooms] = useState([]);
 const [roomTypes, setRoomTypes] = useState([]);
 const [loading, setLoading] = useState(true);
 const [time, setTime] = useState(fmtTime());
 const [checkinBooking, setCheckinBooking] = useState(null);
 const [showRoomBooking, setShowRoomBooking] = useState(false);
 const [showGroupBooking, setShowGroupBooking] = useState(false);
 const [showCorpBooking, setShowCorpBooking] = useState(false);
 const [showNewBooking, setShowNewBooking] = useState(false);

 // Clock
 useEffect(() => {
 const t = setInterval(() => setTime(fmtTime()), 30000);
 return () => clearInterval(t);
 }, []);

 const load = useCallback(async () => {
 setLoading(true);
 try {
 const [b, r, rt] = await Promise.all([
 fetch(`${API}/bookings`).then(x=>x.json()),
 fetch(`${API}/rooms`).then(x=>x.json()),
 fetch(`${API}/rooms/types`).then(x=>x.json()),
 ]);
 setBookings(Array.isArray(b)?b:[]);
 setRooms(Array.isArray(r)?r:[]);
 setRoomTypes(Array.isArray(rt)?rt:[]);
 } catch {}
 finally { setLoading(false); }
 }, []);

 useEffect(() => { load(); }, [load]);

 // Computed stats
 
 // ── CHECKOUT → AUTO HK TASK ──────────────────────────────────────────────
 const handleCheckIn = async (booking) => {
 try {
 await fetch(`${API}/bookings/${booking.id}`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ booking_status:'Checked In', pax_count: booking.pax_count }),
 });
 setBookings(p=>p.map(b=>b.id===booking.id?{...b,booking_status:'Checked In'}:b));
 alert(' Checked In: '+( booking.guest_name||booking.company_name));
 } catch(e) { alert('Error: '+e.message); }
 };

 const handleCheckOut = async (booking) => {
 if (!window.confirm('Check out '+( booking.guest_name||booking.company_name)+'?')) return;
 try {
 // Update booking status
 await fetch(`${API}/bookings/${booking.id}`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ booking_status:'Checked Out', pax_count: booking.pax_count }),
 });
 // Auto-create housekeeping task
 if (booking.room_id) {
 await fetch(`${API}/housekeeping`, {
 method:'POST', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ room_id: booking.room_id, task_type:'Cleaning', priority:'High' }),
 });
 // Mark room as Vacant Dirty
 await fetch(`${API}/rooms/${booking.room_id}/status`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ status:'Vacant Dirty' }),
 });
 }
 setBookings(p=>p.map(b=>b.id===booking.id?{...b,booking_status:'Checked Out'}:b));
 alert(' Checked Out! Housekeeping task auto-created.');
 } catch(e) { alert('Error: '+e.message); }
 };

 const todayArrivals = bookings.filter(b => b.start_date?.slice(0,10)===todayStr && !['Cancelled','No Show'].includes(b.booking_status));
 const todayDepartures = bookings.filter(b => b.end_date?.slice(0,10)===todayStr && b.booking_status==='Checked In');
 const inHouse = bookings.filter(b => b.booking_status==='Checked In');
 const dayPicnic = bookings.filter(b => b.booking_type?.includes('Picnic') && b.start_date?.slice(0,10)===todayStr);
 const weddings = bookings.filter(b => b.booking_type?.includes('Wedding') && b.start_date?.slice(0,10)===todayStr);
 const corporate = bookings.filter(b => b.booking_type?.includes('Corporate') && b.start_date?.slice(0,10)===todayStr);
 const pendingPayments = bookings.filter(b => parseFloat(b.balance_amount||0)>0 && b.booking_status!=='Cancelled');
 const vacantClean = rooms.filter(r=>r.status==='Vacant Clean');
 const dirty = rooms.filter(r=>r.status==='Vacant Dirty');
 const maintenance = rooms.filter(r=>r.status==='Maintenance');
 const totalDayPicnicPax = dayPicnic.reduce((s,b)=>s+parseInt(b.pax_count||0),0);
 const totalWeddingPax = weddings.reduce((s,b)=>s+parseInt(b.pax_count||0),0);

 const handleCheckin = async (booking) => {
 try {
 await fetch(`${API}/bookings/${booking.id}`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ booking_status:'Checked In' }),
 });
 setBookings(p=>p.map(b=>b.id===booking.id?{...b,booking_status:'Checked In'}:b));
 setCheckinBooking(null);
 } catch { alert('Check-in failed'); }
 };

 const TABS = [
 { id:'dashboard', label:' Dashboard' },
 { id:'arrivals', label:' Arrivals' },
 { id:'departures', label:' Departures' },
 { id:'inhouse', label:' In-House' },
 { id:'day_picnic', label:' Day Picnic' },
 { id:'requests', label:' Guest Requests' },
 { id:'maintenance', label:' Maintenance' },
 { id:'lost_found', label:' Lost & Found' },
 ];

 return (
 <div style={{ display:'flex', height:'100vh', background:C.bg, color:C.text, fontFamily:'Inter,sans-serif', overflow:'hidden' }}>

 {/* Left Nav */}
 <div style={{ width:200, background:C.surface, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', flexShrink:0, overflowY:'auto' }}>
 <div style={{ padding:'16px 14px', borderBottom:`1px solid ${C.border}` }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <div>
 <div style={{ fontSize:13, fontWeight:800, color:C.accent }}> Front Office</div>
 <button onClick={()=>setShowNewBooking(true)} style={{ marginTop:6, width:'100%', padding:'8px', background:'#22C55E', border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700 }}>+ New Booking</button>
 <div style={{ fontSize:10, color:C.text3, marginTop:2 }}>{time} · {new Date().toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}</div>
 </div>
 <button onClick={handleLogout} title="Logout" style={{ background:'rgba(224,80,80,.1)', border:'1px solid rgba(224,80,80,.3)', borderRadius:6, color:C.red, cursor:'pointer', fontSize:12, padding:'4px 8px', fontWeight:600 }}></button>
 </div>
 </div>
 <nav style={{ flex:1, padding:'8px 8px' }}>
 {TABS.map(t=>(
 <button key={t.id} onClick={()=>setActiveTab(t.id)}
 style={{ width:'100%', padding:'9px 12px', background:activeTab===t.id?`${C.accent}20`:'transparent', border:`1px solid ${activeTab===t.id?C.accent:'transparent'}`, borderRadius:8, color:activeTab===t.id?C.accent:C.text2, cursor:'pointer', fontSize:12, fontWeight:600, textAlign:'left', marginBottom:2 }}>
 {t.label}
 </button>
 ))}
 </nav>
 </div>

 {/* Main Content */}
 <div style={{ flex:1, overflowY:'auto', padding:20 }}>

 {/* ── DASHBOARD ── */}
 {activeTab==='dashboard' && (
 <div>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
 <div>
 <div style={{ fontSize:20, fontWeight:800 }}>Front Office Command Center</div>
 <div style={{ fontSize:12, color:C.text2, marginTop:2 }}>{new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>
 </div>
 <button onClick={load} style={{ padding:'8px 16px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}> Refresh</button>
 </div>

 {/* KPIs */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
 <KpiCard label="Today's Arrivals" value={todayArrivals.length} icon="" color={C.accent} onClick={()=>setActiveTab('arrivals')} />
 <KpiCard label="Today's Departures" value={todayDepartures.length} icon="" color={C.purple} onClick={()=>setActiveTab('departures')} />
 <KpiCard label="In-House Guests" value={inHouse.reduce((s,b)=>s+parseInt(b.pax_count||0),0)} icon="" color={C.blue} onClick={()=>setActiveTab('inhouse')} />
 <KpiCard label="Pending Payments" value={pendingPayments.length} icon="" color={C.red} onClick={()=>setActiveTab('arrivals')} />
 </div>

 <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
 <KpiCard label="Day Picnic Pax" value={totalDayPicnicPax} icon="" color={C.teal} onClick={()=>setActiveTab('day_picnic')} />
 <KpiCard label="Wedding Guests" value={totalWeddingPax} icon="" color={C.purple} />
 <KpiCard label="Corporate Groups" value={corporate.length} icon="" color={C.blue} />
 <KpiCard label="Rooms Available" value={vacantClean.length} icon="" color={C.green} />
 </div>

 {/* Room Status */}
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:16, marginBottom:20 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:12 }}> Room Status</div>
 <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
 {[
 {label:'Vacant Clean', value:vacantClean.length, color:C.green, },
 {label:'Occupied', value:rooms.filter(r=>r.status==='Occupied').length, color:C.blue, },
 {label:'Dirty', value:dirty.length, color:C.amber, },
 {label:'Maintenance', value:maintenance.length, color:C.red, },
 ].map(s=>(
 <div key={s.label} style={{ background:C.surface2, borderRadius:10, padding:'12px 14px', textAlign:'center' }}>
 <div style={{ fontSize:20 }}>{s.icon}</div>
 <div style={{ fontSize:24, fontWeight:900, color:s.color, marginTop:4 }}>{s.value}</div>
 <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{s.label}</div>
 </div>
 ))}
 </div>
 </div>

 {/* Quick Actions */}
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:16, marginBottom:20 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:12 }}> Quick Actions</div>
 <div style={{ display:'grid', gridTemplateColumns:'repeat(8,1fr)', gap:10 }}>
 {[
 {label:'Arrivals', color:C.accent, tab:'arrivals'},
 {label:'Departures', color:C.purple, tab:'departures'},
 {label:'Walk-in', color:C.blue, action:()=>setShowNewBooking(true)},
 {label:'Room Map', color:C.teal, tab:'inhouse'},
 {label:'Requests', color:C.amber, tab:'requests'},
 {label:'Maintenance', color:C.red, tab:'maintenance'},
 {label:'Lost & Found', color:C.text2, tab:'lost_found'},
 {label:'Day Picnic', color:C.teal, tab:'day_picnic'},
 ].map(a=>(
 <QuickAction key={a.label} icon={a.icon} label={a.label} color={a.color} onClick={()=>a.action?a.action():setActiveTab(a.tab)} />
 ))}
 </div>
 </div>

 {/* Today's Arrivals Preview */}
 {todayArrivals.length > 0 && (
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:16 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:12 }}> Today's Arrivals ({todayArrivals.length})</div>
 {todayArrivals.slice(0,3).map(b=>(
 <BookingCard key={b.id} booking={b} onCheckin={b=>setCheckinBooking(b)} />
 ))}
 {todayArrivals.length>3 && (
 <button onClick={()=>setActiveTab('arrivals')} style={{ width:'100%', padding:'10px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}>
 View all {todayArrivals.length} arrivals →
 </button>
 )}
 </div>
 )}
 </div>
 )}

 {/* ── ARRIVALS ── */}
 {activeTab==='arrivals' && (
 <div>
 <div style={{ fontWeight:800, fontSize:18, marginBottom:20 }}> Today's Arrivals ({todayArrivals.length})</div>
 {loading && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>Loading…</div>}
 {!loading && todayArrivals.length===0 && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>No arrivals today</div>}
 {!loading && todayArrivals.map(b=>(
 <BookingCard key={b.id} booking={b} onCheckin={b=>setCheckinBooking(b)} />
 ))}
 </div>
 )}

 {/* ── DEPARTURES ── */}
 {activeTab==='departures' && (
 <div>
 <div style={{ fontWeight:800, fontSize:18, marginBottom:20 }}> Today's Departures ({todayDepartures.length})</div>
 {todayDepartures.length===0 && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>No departures today</div>}
 {todayDepartures.map(b=>{
 const bst = {color:C.purple, bg:'rgba(128,96,192,.2)'};
 return (
 <div key={b.id} style={{ background:C.surface2, border:`1px solid ${C.border}`, borderRadius:10, padding:12, marginBottom:8 }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
 <div>
 <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{b.guest_name||b.company_name||'—'}</div>
 <div style={{ fontSize:11, color:C.text2 }}>{b.booking_type} · {b.pax_count} pax · {b.booking_code}</div>
 </div>
 <div style={{ fontSize:13, fontWeight:700, color:parseFloat(b.balance_amount)>0?C.red:C.green }}>
 {parseFloat(b.balance_amount)>0 ? `Bal: ${inr(b.balance_amount)}` : ' Settled'}
 </div>
 </div>
 <div style={{ display:'flex', gap:6 }}>
 <button style={{ flex:1, padding:'6px', background:`${C.purple}20`, border:`1px solid ${C.purple}44`, borderRadius:6, color:C.purple, cursor:'pointer', fontSize:11, fontWeight:700 }}> Check Out</button>
 <button style={{ padding:'6px 10px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:11 }}> Invoice</button>
 <button style={{ padding:'6px 10px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:11 }}> Feedback</button>
 </div>
 </div>
 );
 })}
 </div>
 )}

 {/* ── IN-HOUSE ── */}
 {activeTab==='inhouse' && (
 <div>
 <div style={{ fontWeight:800, fontSize:18, marginBottom:20 }}> In-House Guests ({inHouse.length} bookings)</div>
 {inHouse.length===0 && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>No guests checked in</div>}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>
 {inHouse.map(b=>(
 <div key={b.id} style={{ background:C.surface2, border:`1px solid ${C.blue}44`, borderRadius:12, padding:14 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:4 }}>{b.guest_name||b.company_name||'—'}</div>
 <div style={{ fontSize:12, color:C.text2 }}>{b.booking_type}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{b.pax_count} pax · Checkout: {fmtDate(b.end_date)}</div>
 <div style={{ marginTop:10, display:'flex', gap:6 }}>
 <button style={{ flex:1, padding:'5px', background:`${C.blue}20`, border:`1px solid ${C.blue}44`, borderRadius:6, color:'#60A0E0', cursor:'pointer', fontSize:11, fontWeight:600 }}> Folio</button>
 <button style={{ flex:1, padding:'5px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:11 }}> Request</button>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* ── DAY PICNIC ── */}
 {activeTab==='day_picnic' && (
 <div>
 <div style={{ fontWeight:800, fontSize:18, marginBottom:20 }}> Day Picnic Today ({dayPicnic.length} groups · {totalDayPicnicPax} pax)</div>
 {dayPicnic.length===0 && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>No day picnics today</div>}
 {dayPicnic.map(b=>(
 <div key={b.id} style={{ background:C.surface2, border:`1px solid ${C.teal}44`, borderRadius:12, padding:14, marginBottom:10 }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
 <div>
 <div style={{ fontWeight:700, fontSize:14, color:C.text }}>{b.guest_name||b.company_name||'—'}</div>
 <div style={{ fontSize:12, color:C.text2 }}>{b.pax_count} pax · {b.source} · {b.booking_code}</div>
 <div style={{ fontSize:11, color:C.text3 }}>Salesperson: {b.salesperson||'—'}</div>
 </div>
 <div style={{ textAlign:'right' }}>
 <div style={{ fontSize:14, fontWeight:700, color:C.accent }}>{inr(b.total_amount)}</div>
 {parseFloat(b.balance_amount)>0 && <div style={{ fontSize:11, color:C.red }}>Bal: {inr(b.balance_amount)}</div>}
 </div>
 </div>
 <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
 {[' Wristbands',' Meal Coupons',' Coordinator',' Welcome Board'].map(a=>(
 <button key={a} style={{ padding:'6px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:11, fontWeight:600 }}>{a}</button>
 ))}
 </div>
 </div>
 ))}
 </div>
 )}

 {/* ── GUEST REQUESTS ── */}
 {activeTab==='requests' && <GuestRequests />}

 {/* ── MAINTENANCE ── */}
 {activeTab==='maintenance' && <MaintenanceTickets />}

 {/* ── LOST & FOUND ── */}
 {activeTab==='lost_found' && <LostFound />}
 </div>

 {/* Check-in Modal */}
 {checkinBooking && (
 <CheckinModal booking={checkinBooking} rooms={rooms} roomTypes={roomTypes}
 onConfirm={(data)=>handleCheckin(checkinBooking,data)}
 onClose={()=>setCheckinBooking(null)} />
 )}
 </div>
 );
}
